import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import modal from "../util/modal";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEdit, faSync, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { organizationList } from "../../api/api";
import { deptList, loadCategory, loadPriority, loadTicketStatus } from "../../api/settingApi";

const Settings = () => {

  const user = JSON.parse(sessionStorage.getItem('lgusr'));
  const isSuAdmin = user?.role === 'ADMINISTRATOR';
  const orgId = user?.orgId;
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [depts, setDepts] = useState([]);
  const [pcategories, setPcategories] = useState([]);
  const [ticketPriority, setTicketPriority] = useState([]);
  const [ticketProgress, setTicketProgress] = useState([]);
  
  const formatDate = (date) => {
    return moment(date).format('DD, MMM-YYYY hh:mma');
  };

  const getStatus = (status) => {
    return status == 1 ? 'Active' : 'Inactive';
  };

  const fetchData = async () => {
    try {
      if (isSuAdmin) {
        const orgResponse = await organizationList();
        setOrgs(orgResponse.data);
      }
      const deptResponse = await deptList();
      setDepts(deptResponse.data);

      const categories = await loadCategory(orgId);
      setPcategories(categories.data);

      const priorityList = await loadPriority(orgId);
      setTicketPriority(priorityList.data);

      const ticketProgressStatusList = await loadTicketStatus(orgId);
      setTicketProgress(ticketProgressStatusList.data);

    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      
    }
  };

  useEffect(() => {
    const handlePageLoad = () => {
      fetchData();
    };
    if (document.readyState === "complete") {
      handlePageLoad();
    } else {
      window.addEventListener("load", handlePageLoad);
    }
    return () => {
      window.removeEventListener("load", handlePageLoad);
    };
  }, []);

  return (
    <div className="bg-white p-8">
      <h1 className="text-3xl font-extrabold text-blue-600 mb-6">Settings</h1>
      <div className="space-y-8">
        {isSuAdmin && (
          <section>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-orange-600">Organization List</h2>
              <button className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600" onClick={() => navigate('/settings/new-org')}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
            <div className="bg-blue-50 shadow-lg rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Updated At</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orgs.map((org) => (
                    <tr key={org.id}>
                      <td className="px-6 py-4 text-sm text-gray-900">{org.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(org.updatedAt)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{getStatus(org.status)}</td>
                      <td className="px-6 py-4 text-right text-sm space-x-2">
                        <button className="text-blue-600 hover:underline"><FontAwesomeIcon icon={faEye} /></button>
                        <button className="text-green-600 hover:underline"><FontAwesomeIcon icon={faEdit} /></button>
                        <button className="text-yellow-600 hover:underline"><FontAwesomeIcon icon={faSync} /></button>
                        <button className="text-red-600 hover:underline"><FontAwesomeIcon icon={faTrash} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Department List */}
        <section>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-orange-600">Department List</h2>
            <button className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600" onClick={() => navigate('/settings/new-dept')}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          <div className="bg-blue-50 shadow-lg rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Mentor</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Updated At</th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {depts.map((dept) => (
                  <tr key={dept.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{dept.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{dept.mentorName || 'No Mentor'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{dept.organizationName}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{dept.status}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{dept.updatedAt}</td>
                    <td className="px-6 py-4 text-right text-sm space-x-2">
                      <button className="text-blue-600 hover:underline"><FontAwesomeIcon icon={faEye} /></button>
                      <button className="text-green-600 hover:underline"><FontAwesomeIcon icon={faEdit} /></button>
                      <button className="text-yellow-600 hover:underline"><FontAwesomeIcon icon={faSync} /></button>
                      <button className="text-red-600 hover:underline"><FontAwesomeIcon icon={faTrash} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Problem Categories */}
        <section>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-orange-600">Problem Categories</h2>
            <button className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600" onClick={() => navigate('/settings/new-category')}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          <div className="bg-blue-50 shadow-lg rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Updated At</th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pcategories.map((pcategorie) => (
                  <tr key={pcategorie.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{pcategorie.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{pcategorie.organizationName}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{pcategorie.status}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{pcategorie.updatedAt}</td>
                    <td className="px-6 py-4 text-right text-sm space-x-2">
                      <button className="text-blue-600 hover:underline"><FontAwesomeIcon icon={faEye} /></button>
                      <button className="text-green-600 hover:underline"><FontAwesomeIcon icon={faEdit} /></button>
                      <button className="text-yellow-600 hover:underline"><FontAwesomeIcon icon={faSync} /></button>
                      <button className="text-red-600 hover:underline"><FontAwesomeIcon icon={faTrash} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        {/* Ticket Priority */}
        <section>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-orange-600">Ticket Priority</h2>
            <button className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600" onClick={() => navigate('/settings/new-ticket-priority')}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          <div className="bg-blue-50 shadow-lg rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Updated At</th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ticketPriority.map((ph) => (
                  <tr key={ph.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{ph.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{ph.organizationName}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{ph.status}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{ph.updatedAt}</td>
                    <td className="px-6 py-4 text-right text-sm space-x-2">
                      <button className="text-blue-600 hover:underline"><FontAwesomeIcon icon={faEye} /></button>
                      <button className="text-green-600 hover:underline"><FontAwesomeIcon icon={faEdit} /></button>
                      <button className="text-yellow-600 hover:underline"><FontAwesomeIcon icon={faSync} /></button>
                      <button className="text-red-600 hover:underline"><FontAwesomeIcon icon={faTrash} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        {/* Ticket Progress Status */}
        <section>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-orange-600">Ticket Progress Status</h2>
            <button className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600" onClick={() => navigate('/settings/new-ticket-status')}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          <div className="bg-blue-50 shadow-lg rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Updated At</th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ticketProgress.map((ph) => (
                  <tr key={ph.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{ph.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{ph.organizationName}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{ph.status}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{ph.updatedAt}</td>
                    <td className="px-6 py-4 text-right text-sm space-x-2">
                      <button className="text-blue-600 hover:underline"><FontAwesomeIcon icon={faEye} /></button>
                      <button className="text-green-600 hover:underline"><FontAwesomeIcon icon={faEdit} /></button>
                      <button className="text-yellow-600 hover:underline"><FontAwesomeIcon icon={faSync} /></button>
                      <button className="text-red-600 hover:underline"><FontAwesomeIcon icon={faTrash} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Settings;
